import { WarningIcon } from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { SuperAdminGuard } from '@/components/guards/permission-guards';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { RoleFormData } from '@/types/sheets';

import RolePermissions from './shared/RolePermissions';

interface Permission {
  resource: string;
  actions: string[];
}

interface RoleUpdateSheetProps {
  role: any;
  onRoleUpdated?: () => Promise<void>;
}

const RoleUpdateSheet: React.FC<RoleUpdateSheetProps> = ({
  role,
  onRoleUpdated,
}) => {
  const api = useEnhancedApi();
  const { hideSheet, showSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Transform the permissions array into the format expected by the form
  const transformPermissionsToFormFormat = React.useCallback(
    (permissions: Permission[] = []) => {
      const formattedPermissions: { [resource: string]: string[] } = {};
      permissions.forEach(permission => {
        formattedPermissions[permission.resource] = permission.actions;
      });
      return formattedPermissions;
    },
    []
  );

  const { handleSubmit, control, reset, setValue } = useForm<RoleFormData>({
    defaultValues: {
      name: '',
      description: '',
      permissions: {},
    },
  });

  // Update form when role changes
  useEffect(() => {
    if (role) {
      const formattedPermissions = transformPermissionsToFormFormat(
        role.permissions || []
      );

      setValue('name', role.name || '');
      setValue('description', role.description || '');
      setValue('permissions', formattedPermissions);

      // Reset form with the new values to ensure proper display
      reset({
        name: role.name || '',
        description: role.description || '',
        permissions: formattedPermissions,
      });
    }
  }, [role?._id, role?.name, role?.description, role?.permissions]); // Use specific dependencies instead of whole role object

  const formatPermissionsForSubmission = (permissions: {
    [resource: string]: string[];
  }) => {
    return Object.entries(permissions).map(([resource, actions]) => ({
      resource,
      actions,
    }));
  };

  const handleUpdateRole = handleSubmit(async (formData: RoleFormData) => {
    const hasPermissions = Object.keys(formData.permissions).length > 0;

    if (!hasPermissions) {
      showError('Please select at least one resource permission');
      return;
    }

    setIsLoading(true);
    try {
      // Sanitize role name by removing spaces
      const sanitizedName = (formData.name || '').replace(/\s+/g, '');

      const rolePayload = {
        roleId: role?._id,
        name: sanitizedName,
        description: formData.description,
        permissions: formatPermissionsForSubmission(formData.permissions),
      };

      const response = await api.put('/roles', rolePayload);

      if (response.success) {
        showSuccess(response.message || 'Role updated successfully');
        reset();
        hideSheet('role-update');
        // Refresh the roles list if callback provided
        if (onRoleUpdated) {
          await onRoleUpdated();
        }
      } else {
        showError(response.error?.message || 'Role update failed');
      }
    } catch {
      showError('An error occurred while updating the role');
    } finally {
      setIsLoading(false);
    }
  });

  const handleDeleteRole = async () => {
    if (!role?.name) return;

    setDeleteLoading(true);
    try {
      const response = await api.delete(`/roles/${role.name}`);
      if (response.success) {
        showSuccess(response.message || 'Role deleted successfully');
        hideSheet('role-update');
        hideSheet('delete-role-confirm');
        // Refresh the roles list if callback provided
        if (onRoleUpdated) {
          await onRoleUpdated();
        }
      } else {
        showError(response.error?.message || 'Failed to delete role');
      }
    } catch {
      showError('An error occurred while deleting the role');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    hideSheet('role-update');
  };

  const showDeleteConfirmation = () => {
    showSheet({
      id: 'delete-role-confirm',
      type: 'confirmation',
      title: 'Delete Role',
      content: (
        <div className='py-4'>
          <div className='flex gap-4 items-start mb-4'>
            <WarningIcon size={24} className='text-amber-600 mt-1' />
            <div>
              <h3 className='font-semibold text-[#1A1A1A] text-[16px]'>
                Delete Role
              </h3>
              <p className='text-[#4E4E4E] text-[14px] mt-1'>
                Are you sure you want to delete role "{role?.name}"? This action
                cannot be undone.
              </p>
            </div>
          </div>
        </div>
      ),
      buttons: [
        {
          id: 'delete',
          label: 'Yes, Delete',
          onClick: handleDeleteRole,
          color: 'destructive',
          hierarchy: 'primary',
          loading: deleteLoading,
          disabled: deleteLoading,
        },
        {
          id: 'cancel',
          label: 'Cancel',
          onClick: () => hideSheet('delete-role-confirm'),
          color: 'gray',
          hierarchy: 'secondary',
        },
      ],
    });
  };

  // Expose handlers for dynamic button system
  React.useEffect(() => {
    (window as any).roleUpdateHandlers = {
      handleUpdateRole,
      handleCancel,
      showDeleteConfirmation,
      isLoading,
    };

    return () => {
      // Cleanup on unmount
      delete (window as any).roleUpdateHandlers;
    };
  }, [handleUpdateRole, handleCancel, showDeleteConfirmation, isLoading]);

  return (
    <SuperAdminGuard>
      <div className='space-y-6'>
        <form onSubmit={handleUpdateRole}>
          <div className='mb-6 space-y-4'>
            <Controller
              name='name'
              control={control}
              rules={{
                required: 'Role name is required',
                minLength: {
                  value: 3,
                  message: 'Role name must be at least 3 characters',
                },
              }}
              render={({ field, fieldState }) => (
                <Input
                  label='Role Name'
                  placeholder='Enter role name'
                  value={field.value}
                  onChange={e =>
                    field.onChange(e.target.value.replace(/\s+/g, ''))
                  }
                  error={fieldState?.error?.message}
                  className='w-full'
                  disabled={isLoading}
                />
              )}
            />

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <Textarea
                    id='description'
                    placeholder='Enter role description (optional)'
                    value={field.value}
                    onChange={field.onChange}
                    className='w-full'
                    disabled={isLoading}
                    rows={3}
                  />
                )}
              />
            </div>
          </div>

          <RolePermissions
            control={control}
            initialPermissions={transformPermissionsToFormFormat(
              role?.permissions
            )}
          />
        </form>
      </div>
    </SuperAdminGuard>
  );
};

export default RoleUpdateSheet;
